import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { formatDateForInput, getParamCaseInsensitive, normalizeCountryId, parseCanadianRoutingNumber } from './helpers';
import { FullPageSpinner } from '#/Scripts/shared/components/FullPageSpinner';
import { CANADA_SIN_VALIDATION, PREVIOUSLY_ENTERED, US_TAX_ID_VALIDATION } from '#/Scripts/shared/form/validation';
import { BankInformationForm } from '#/Scripts/sign-up/components/BankInformationForm/BankInformationForm';
import epayImg from '#images/epay.png';
import pciImg from '#images/pci.png';
import { ApiClient } from '#payment-page/utilities/ApiClient';
import { useKeyDown } from '#shared/hooks/useKeyDown';
import { AccountInformationForm } from '#sign-up/components/AccountInformationForm/AccountInformationForm';
import { ErrorAlert } from '#sign-up/components/common/ErrorAlert/ErrorAlert';
import { CompanyInformationForm } from '#sign-up/components/CompanyInformationForm/CompanyInformationForm';
import { ContactInformationForm } from '#sign-up/components/ContactInformationForm/ContactInformationForm';
import { ControllerInformationForm } from '#sign-up/components/ControllerInformationForm/ControllerInformationForm';
import { Faq } from '#sign-up/components/SignUp/FAQ';
import { ControllerModel, Country, SignupFormValues, SignupModel, SignupStep } from '#sign-up/components/SignUp/types';
import { SignUpNavigation } from '#sign-up/components/SignUpNavigation/SignUpNavigation';
import { SignUpTimeline } from '#sign-up/components/SignUpTimeline/SignUpTimeline';
import '#sign-up/Signup.css';

type SignUpResponseModel = {
  Success: boolean;
  ErrorMessage?: string;
  ErrorMessageField?: string;
};

interface SignUpProps {
  model: SignupModel;
}

export const SignUp = ({ model }: SignUpProps) => {
  const initialStep = model.CurrentStep ?? SignupStep.MainContact;
  const [step, setStep] = useState<SignupStep>(initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasCompletedCompanyStep = initialStep > SignupStep.Company;
  const hasCompletedControllersStep = initialStep > SignupStep.Controllers;
  const hasCompletedAccountsStep = initialStep > SignupStep.Accounts;
  const hasCompletedFinishStep = !!model.Completed;

  // Extract query params from URL (case-insensitive to handle prefill links with varying casing)
  const opportunityId = getParamCaseInsensitive('opportunityId');
  const key = getParamCaseInsensitive('key');

  // Determine stored country for prefill logic
  const storedCountry = normalizeCountryId(model.CountryId) ?? Country.UnitedStates;

  const isCanadaForPrefill = storedCountry === Country.Canada;

  // Parse Canadian routing number into institution/transit components for prefill
  const { institutionNumber, transitNumber } = isCanadaForPrefill
    ? parseCanadianRoutingNumber(model.TrustRoutingNumber)
    : { institutionNumber: '', transitNumber: '' };

  const form = useForm<SignupFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      FirstName: model.FirstName ?? '',
      LastName: model.LastName ?? '',
      EmailAddress: model.EmailAddress ?? '',
      PhoneNumber: model.PhoneNumber ?? '',

      CompanyName: model.CompanyName ?? '',
      DoingBusinessAs: model.DoingBusinessAs ?? '',
      StreetAddress: model.StreetAddress ?? '',
      Suite: model.Suite ?? '',
      City: model.City ?? '',
      State: model.State ?? '',

      CountryId: hasCompletedCompanyStep ? storedCountry : Country.UnitedStates,
      PostalCode: model.PostalCode ?? '',
      LegalEntityTypeId: hasCompletedCompanyStep ? (model.LegalEntityTypeId ?? undefined) : undefined,
      Url: model.Url ?? '',
      IsPublicEntity: hasCompletedCompanyStep ? model.IsPublicEntity : undefined,
      AccountCategoryId: hasCompletedCompanyStep ? (model.AccountCategoryId ?? undefined) : undefined,
      HasAcceptedCreditCards: hasCompletedCompanyStep ? model.HasAcceptedCreditCards : undefined,

      TaxId: model.TaxId ?? '',
      StockExchange: model.StockExchange ?? '',
      StockTicker: model.StockTicker ?? '',

      TrustAccountTypeId: hasCompletedAccountsStep ? (model.TrustAccountTypeId ?? undefined) : undefined,
      // For Canada, TrustRoutingNumber is composed from InstitutionNumber + TransitNumber on submit
      TrustRoutingNumber: isCanadaForPrefill ? '' : (model.TrustRoutingNumber ?? ''),
      InstitutionNumber: institutionNumber,
      TransitNumber: transitNumber,
      TrustAccountNumber: model.TrustAccountNumber ?? '',
      OperationalAccountTypeId: hasCompletedAccountsStep ? (model.OperationalAccountTypeId ?? undefined) : undefined,
      OperationalRoutingNumber: model.OperationalRoutingNumber ?? '',
      OperationalAccountNumber: model.OperationalAccountNumber ?? '',

      // Credit card fields (Canada only)
      CreditCardName: hasCompletedAccountsStep ? (model.CreditCardName ?? '') : '',
      CreditCardNumber: model.CreditCardNumber ?? '',
      CreditCardMonth:
        hasCompletedAccountsStep && model.CreditCardMonth != null ? String(model.CreditCardMonth).padStart(2, '0') : '',
      CreditCardYear: hasCompletedAccountsStep && model.CreditCardYear != null ? String(model.CreditCardYear) : '',
      CreditCardCvc: '', // Never prefilled - CVV is not stored
      CreditCardPostalCode: hasCompletedAccountsStep ? (model.CreditCardPostalCode ?? '') : '',

      IntegrationTypeId: hasCompletedFinishStep ? (model.IntegrationTypeId ?? undefined) : undefined,
      OtherManagementSystem: hasCompletedFinishStep ? (model.OtherManagementSystem ?? '') : '',
      MonthlyPaperCheckCount: hasCompletedFinishStep ? (model.MonthlyPaperCheckCount ?? '') : '',
      MaximumSingleTransactionAmount: model.MaximumSingleTransactionAmount ?? '',
      MonthlyCreditCardVolume: model.MonthlyCreditCardVolume ?? '',
      Comments: model.Comments ?? '',
      DiscoverySource: model.DiscoverySource ?? '',
      PromotionalCode: model.PromotionalCode ?? '',
      PlanTypeId: hasCompletedFinishStep ? (model.PlanTypeId ?? undefined) : undefined,

      Authorization: model.Authorization ?? false,
      PrivacyPolicyAgreement: model.PrivacyPolicyAgreement ?? false,
      MerchantCategoryCode: 6300, // Always 6300 - Insurance Agents, Brokers and Service

      Controllers: model.Owners?.length
        ? model.Owners.map((owner) => ({
            ...owner,
            // For prefill, only populate SSN if the user has not progressed past the Controllers step (where SSN is collected) to avoid populating with invalid/placeholder values.
            //  Additionally, if it's a public entity, do not prefill SSN as it's not applicable.
            SocialSecurityNumber: (() => {
              if (!hasCompletedControllersStep) {
                return owner.SocialSecurityNumber ?? '';
              }

              if (model.IsPublicEntity) {
                return '';
              }

              return owner.SocialSecurityNumber;
            })(),
            DateOfBirth: formatDateForInput(owner.DateOfBirth),
            IsOwner: hasCompletedControllersStep ? owner.IsOwner : undefined,
            CountryId: hasCompletedControllersStep ? normalizeCountryId(owner.CountryId) : undefined,
            State: hasCompletedControllersStep ? owner.State : '',
          }))
        : [
            {
              FirstName: '',
              LastName: '',
              DateOfBirth: '',
              JobTitle: '',
              IsOwner: undefined,
              SocialSecurityNumber: '',
              EmailAddress: '',
              PhoneNumber: '',
              StreetAddress: '',
              Suite: '',
              City: '',
              State: '',
              CountryId: Country.UnitedStates,
              Country: '',
              PostalCode: '',
              Key: '',
            },
          ],
    },
  });

  const onBack = () => {
    setStep((prev) => (prev > SignupStep.MainContact ? prev - 1 : prev));
  };

  const onSubmit = async (data: SignupFormValues): Promise<boolean> => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Map Controllers to Owners; strip frontend-only Canada routing fields from payload
      const { Controllers, InstitutionNumber, TransitNumber, ...formData } = data;
      let Owners = Controllers?.map((controller: ControllerModel) => controller) || [];

      // Clear invalid SSN values that may have bypassed client-side validation during navigation
      Owners = Owners.map((owner) => {
        const ssn = owner.SocialSecurityNumber;
        if (ssn === null || ssn === undefined || ssn === PREVIOUSLY_ENTERED || ssn.trim() === '') return owner;
        const isCanada = owner.CountryId === Country.Canada;
        const pattern = isCanada ? CANADA_SIN_VALIDATION.pattern : US_TAX_ID_VALIDATION.pattern;
        if (!pattern.test(ssn.trim())) {
          return { ...owner, SocialSecurityNumber: '' };
        }
        return { ...owner, SocialSecurityNumber: ssn.trim().replaceAll('-', '') };
      });

      // Clear irrelevant fields based on company type
      if (formData.IsPublicEntity === true) {
        // Public entity: clear SSN from controllers
        Owners = Owners.map((owner) => ({
          ...owner,
          SocialSecurityNumber: '',
        }));
      } else if (formData.IsPublicEntity === false) {
        // Private entity: clear stock fields
        formData.StockExchange = null;
        formData.StockTicker = null;
      }

      const isCanada = formData.CountryId === Country.Canada;

      if (isCanada) {
        // Combine institution + transit numbers into the canonical Canadian routing number
        formData.TrustRoutingNumber = `0${InstitutionNumber}${TransitNumber}`;
        // Canada uses credit card for subscription — mirror trust account type so the required field is satisfied,
        // and clear the routing/account number fields that don't apply
        formData.OperationalAccountTypeId = formData.TrustAccountTypeId;
        formData.OperationalRoutingNumber = null;
        formData.OperationalAccountNumber = null;
        // Strip spaces and dashes from credit card postal code before sending to backend
        if (formData.CreditCardPostalCode != null) {
          formData.CreditCardPostalCode = formData.CreditCardPostalCode.replaceAll(/[\s-]/g, '');
        }
      } else {
        // US uses bank accounts — clear credit card fields
        formData.CreditCardName = null;
        formData.CreditCardNumber = null;
        formData.CreditCardMonth = null;
        formData.CreditCardYear = null;
        formData.CreditCardCvc = null;
        formData.CreditCardPostalCode = null;
      }

      // Strip undefined fields from payload
      const cleanPayload = Object.entries({ ...formData, Owners }).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            return { ...acc, [key]: value };
          }
          return acc;
        },
        {} as Record<string, unknown>
      );

      const payload = {
        ...cleanPayload,
        OpportunityId: opportunityId,
        Key: key,
        NextStep: step + 1,
        Completed: step === SignupStep.Finish,
        IsNetworkAccountFlow: false,
      };

      const response = await ApiClient.post<SignUpResponseModel>('/Home/SubmitSignUp', payload);

      if (response.Success) {
        if (step < SignupStep.Finish) {
          setStep((prev) => (prev + 1) as SignupStep);
        } else {
          globalThis.location.reload();
        }
        return true;
      } else {
        // If the error is tied to specific fields, set those errors in the form. Otherwise, show a general error message.
        if (response.ErrorMessageField) {
          const errorFields = response.ErrorMessageField.split(',');

          errorFields.forEach((field) => {
            const trimmedField = field.trim() as keyof SignupFormValues;

            form.setError(trimmedField, {
              type: 'server',
              message: response.ErrorMessage || 'Invalid value.',
            });
          });
          const err = new Error(`${response.ErrorMessageField} - ${response.ErrorMessage}`);
          err.name = 'SignUpFieldError';
          throw err;
        } else if (response.ErrorMessage) {
          setErrorMessage(response.ErrorMessage);
        }

        return false;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'SignUpFieldError') {
        throw error;
      }
      console.error('Signup failed', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitHandler = form.handleSubmit(onSubmit);

  const wrappedSubmitHandler = async (e?: React.BaseSyntheticEvent) => {
    let backendSuccess = false;

    try {
      await submitHandler(e);
      // Check if form submission was successful
      backendSuccess = form.formState.isSubmitSuccessful;
    } catch (e: unknown) {
      // If a server-side field error was set, catch the named error and return early
      // without resetting form state so the error message persists
      if (e instanceof Error && e.name === 'SignUpFieldError') {
        return;
      }
      console.log('Error during form submission', e);
    }

    if (backendSuccess && form.formState.isSubmitSuccessful) {
      form.reset(form.getValues(), {
        keepValues: true,
        keepDirty: false,
        keepErrors: false,
        keepTouched: false,
        keepIsSubmitted: false,
        keepSubmitCount: false,
      });
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLFormElement>) => {
    const name = (e.target as unknown as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).name;
    if (name) {
      form.clearErrors(name as Parameters<typeof form.clearErrors>[0]);
    }
  };

  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollableRef.current?.scrollTo(0, 0);
  }, [step]);

  // Enable Enter key submission, but disable during form submission
  useKeyDown(() => wrappedSubmitHandler(), {
    key: 'Enter',
    enabled: !isSubmitting,
    containerSelector: '.signup',
    allowInTextarea: false,
  });

  const stepComponents = {
    [SignupStep.MainContact]: <ContactInformationForm />,
    [SignupStep.Company]: <CompanyInformationForm />,
    [SignupStep.Controllers]: <ControllerInformationForm />,
    [SignupStep.Accounts]: <BankInformationForm />,
    [SignupStep.Finish]: <AccountInformationForm />,
  };

  return (
    <FormProvider {...form}>
      <div className="signup" ref={scrollableRef}>
        <div className="signup-container">
          <div className="signup-header">
            <img className="signup-epay-logo" src={epayImg} alt="ePayPolicy" />
            Account Application
          </div>
          {errorMessage && <ErrorAlert message={errorMessage} />}
          <div className="signup-main">
            <SignUpTimeline currentStep={step} onStepChange={setStep} />
            {
              <form onSubmit={wrappedSubmitHandler} onChange={handleFormChange}>
                {stepComponents[step]}
              </form>
            }
            <SignUpNavigation
              currentStep={step}
              onBack={onBack}
              onSubmit={wrappedSubmitHandler}
              isSubmitting={isSubmitting}
            />
            {(step === SignupStep.Company || step === SignupStep.Controllers) && <Faq step={step} />}
          </div>
          <img className="pci-logo" src={pciImg} alt="PCI DSS" />
        </div>
        <div className="signup-page-footer">
          <div className="signup-page-footer-contact">
            <a href="mailto:support@epaypolicy.com">support@epaypolicy.com</a>
            <span>&middot;</span>
            <span>844-372-9300</span>
          </div>
          <div className="signup-page-footer-copyright">&copy; {new Date().getFullYear()} ePayPolicy</div>
        </div>
        {/* Spinner overlay for navigation and submission */}
        {isSubmitting && <FullPageSpinner />}
      </div>
    </FormProvider>
  );
};
